import { faUserGroup, faUserLock } from "@fortawesome/free-solid-svg-icons";
import { BidValuesEnum } from "@common/enums/BidValuesEnum.ts";
import { DateTime } from "luxon";

export const NEW_BID_ID = 'new-bid-id';

type iconLabel = {
    icon: string;
    color: string;
};

export const themePaletteSelDir = {
    background: {
        DEFAULT: '#12151f',
        PAPER: '#1a2033',
    },
    font: {
        DEFAULT: '#ffffff',
    },
    mode: 'dark'
};

export const themePaletteCompass = {
    background: {
        DEFAULT: '#e6dfdf',
        PAPER: '#e8e1d7',
    },
    font: {
        DEFAULT: '#222222',
    },
    mode: 'light'
}

// Deprecated, use TransactionTypeEnum instead
export const transactionType = {
    LEAD_RETURN: 'return',
    LEAD_ASSIGN: 'lead-assign',
    LEAD_CHARGE: 'lead-charge',
    LEAD_MIXED: 'lead-mixed',
    SUBSCRIPTION_CREDIT: 'subscription-credits',
    SUBSCRIPTION_TYPE_GOLD: 'gold',
    SUBSCRIPTION_TYPE_SILVER: 'silver',
    SUBSCRIPTION_TYPE_BRONZE: 'bronze',
    ADD_CREDITS: 'add-credits',
};

export const visibilityType = {
    ACTIVITY: 'activity',
    BUYER_LEAD: 'buyerLead',
}

export const colorsType = {
    RED: '#B62A2A',
    ORANGE: '#A9571B',
    STRONG_GREEN: '#12974B',
};

export enum countyBidsCategories {
    ALL_COUNTIES = 'All Counties',
    NOT_VIABLE = 'Not Viable',
    VIABLE = 'Viable',
    LEADING = 'Leading',
}

export const getColorStyleByWinRate = (winRate: number | null, isHighBid: boolean, isSoleHighBidder?: boolean): iconLabel => {
    // Sole highest bidder - Green
    if (isHighBid && isSoleHighBidder === true) {
        return { icon: 'strongGreenCircle', color: colorsType.STRONG_GREEN };
    }

    // Tied for highest bid - Orange
    if (isHighBid && isSoleHighBidder === false) {
        return { icon: 'orangeCircle', color: colorsType.ORANGE };
    }

    // Legacy: isHighBid without isSoleHighBidder info - Green (backward compatibility)
    if (isHighBid) {
        return { icon: 'strongGreenCircle', color: colorsType.STRONG_GREEN };
    }

    // Invalid win rate - Gray
    if (winRate === null || winRate < 0 || winRate > 100) {
        return { icon: 'disabledCircle', color: "#949494" };
    }

    // Not the high bidder - Red
    return { icon: 'redCircle', color: colorsType.RED };
};

type LabelCategoryRange = {
    label: string;
    category: string;
    range: { min: number; max: number };
    color: string;
};

export const IS_PAUSED_TYPE: LabelCategoryRange = {
    label: 'Paused',
    category: 'Not Viable',
    range: { min: 0, max: 0 },
    color: colorsType.RED
};

export const NOT_VIABLE_TYPE: LabelCategoryRange = {
    label: 'Not likely to see much',
    category: 'Not Viable',
    range: { min: 0, max: 100 },
    color: colorsType.RED
};

export const VIABLE_TYPE: LabelCategoryRange = {
    label: 'Decent chances—adjust to stay competitive.',
    category: 'Viable',
    range: { min: 20, max: 80 },
    color: colorsType.ORANGE
};

export const SOLE_LEADING_TYPE: LabelCategoryRange = {
    label: "You're in the lead",
    category: 'Leading',
    range: { min: 80, max: 100 },
    color: colorsType.STRONG_GREEN
};

export const TIED_LEADING_TYPE: LabelCategoryRange = {
    label: 'Questionable',
    category: 'Viable',
    range: { min: 80, max: 100 },
    color: colorsType.ORANGE
};

// Kept for backward compatibility
export const LEADING_TYPE: LabelCategoryRange = {
    label: 'Top position - you are leading!',
    category: 'Leading',
    range: { min: 80, max: 100 },
    color: colorsType.STRONG_GREEN
};

/**
 * Determines the bid status type based on bid position relative to other bidders.
 *
 * Returns a LabelCategoryRange object containing:
 * - label: Display text for the status (e.g., "You're in the lead", "Questionable")
 * - category: Filter category ('Leading', 'Viable', 'Not Viable')
 * - color: Hex color code for the status indicator
 *
 * Status logic:
 * - Green (SOLE_LEADING_TYPE): Sole highest bidder
 * - Orange (TIED_LEADING_TYPE): Tied for highest bid with other bidders
 * - Red (NOT_VIABLE_TYPE): Outbid by other bidders
 * - Red (IS_PAUSED_TYPE): Bid is paused
 *
 * @param isHighBid - Whether this bid matches or exceeds the current high bid
 * @param isPaused - Whether the bid is paused
 * @param isSoleHighBidder - true if sole high bidder, false if tied, undefined for legacy/unknown
 * @returns LabelCategoryRange object with label, category, range, and color
 */
export const getTypeByRange = (isHighBid: boolean, isPaused: boolean, isSoleHighBidder?: boolean) => {
    if (isPaused) return IS_PAUSED_TYPE;

    // Sole highest bidder - Green
    if (isSoleHighBidder === true && isHighBid) {
        return SOLE_LEADING_TYPE;
    }

    // Tied for highest bid (multiple bidders at same high amount) - Orange
    if (isHighBid && isSoleHighBidder === false) {
        return TIED_LEADING_TYPE;
    }

    // Legacy: if isHighBid is true but isSoleHighBidder is undefined (backward compatibility)
    if (isHighBid) return LEADING_TYPE;

    // Not the high bidder - Red (includes $50 default bids)
    return NOT_VIABLE_TYPE;
};

export const getColorByCategory = (category: string) => {
    switch (category) {
        case countyBidsCategories.NOT_VIABLE:
            return colorsType.RED;
        case countyBidsCategories.VIABLE:
            return colorsType.ORANGE;
        case countyBidsCategories.LEADING:
            return colorsType.STRONG_GREEN;
        default:
            return colorsType.RED;
    }
}

export const leadStatus = {
    NEW: 'new',
    TRASH: 'trash',
    SENT: 'sent',
    CONFIRMED: 'confirmed',
    ARCHIVED: 'archived',
    VIEWED: 'viewed',
}
export const disputesColor = {
    PENDING: '#FF9E27',
    APPROVED: '#12974B',
    REJECTED: '#FF8383',
}

export const disputeStatus = {
    PENDING: 'Pending',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
}

const activityVisibility = {
    ADMIN: 'admin',
    PRIVATE: 'private',
    USER: 'user',
}

const activityIcons = {
    admin: { icon: faUserLock, color: 'rgb(1, 177, 244)' },
    private: { icon: faUserLock, color: 'rgb(18, 151, 75)' },
    user: { icon: faUserGroup, color: '#bd01ff' }

}

export const getIconByVisibility = (visibility: string) => {
    if (visibility === activityVisibility.ADMIN) { return activityIcons.admin }
    if (visibility === activityVisibility.PRIVATE) { return activityIcons.private }
    if (visibility === activityVisibility.USER) { return activityIcons.user }
    return { icon: faUserGroup, color: 'gray' } // this should never happen
}

export const buyerLeadTransactionType = {
    CHARGED: 'charged',
    PENDING: 'pending',
    REFUNDED: 'refunded',
}

export function formatToUSD(number: number) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(number);
}

export const bidAmountLimits = {
    LEGACY_MIN: BidValuesEnum.LEGACY_MIN_BID,
    MIN: BidValuesEnum.MIN_BID,
    MAX: BidValuesEnum.MAX_BID,
    STEP: 50
};

const CURRENT_VERSION = 5;

export enum LeadAction {
    ASSIGN = 'ASSIGN',
    CONFIRM = 'CONFIRM',
    CANCEL = 'CANCEL',
    CANCEL_CONFIRM = 'CANCEL_CONFIRM',
    CHARGE = 'CHARGE'
}

export const defaultAppData = {
    session: null,
    loadedLeads: [],
    loadedBuyers: [],
    loggedInUser: null,
    buyerDetailSection: 6,
    filters: {
        leadFilters: {
            search: '',
            status: 'all-new',
            dispute_status: [],
            transaction_status: [],
            limit: 100,
            page: 1,
            offset: 0,
            counties: [],
            categories: [],
            dateRange: [DateTime.now().startOf('day').toJSDate().toISOString(), DateTime.now().endOf('day').toJSDate().toISOString()],
            sortOptions: { sortBy: 'name', order: 'asc' },
            labels: [],
            verified: true,
            unverified: true,
            product: null as string | null,
        },
        buyerLeadFilters: {
            get_archived: false,
            search: '',
            status: '',
            dispute_status: [],
            transaction_status: '',
            limit: 25,
            page: 1,
            offset: 0,
            counties: [],
            dateRange: ['', ''],
        },
        buyerFilters: {
            search: '',
            limit: 25,
            page: 1,
        },
        disputeFilters: {
            search: '',
            status: [],
            dateRange: ['', ''],
            limit: 25,
            page: 1,
        },
        transactionFilters: {
            search: '',
            dateRange: ['', ''],
            limit: 25,
            page: 1,
        },
        adminFilters: {
            search: '',
            limit: 25,
            page: 1,
        }
    },
    role: '',
    disputeRate: 0,
    statesIDs: [],
    version: CURRENT_VERSION,
};

export const WARNING_YELLOW = '#eed202';
export const WARNING_RED = '#B62A2A';
export const UNPROTECTED_PATHS = ['/login', '/register', '/complete-registration', '/password-reset', '/change-password', '/confirm-email-change', '/']
export const DISPUTE_REASONS = ['Unimproved Land', 'Listed on MLS or FSBO.com', 'Mobile Home in park', 'Wholesaler', 'Not the owner', 'Not Selling', 'No Contact']
// days to allow reject on static disputes
export const STATIC_REJECT_RANGE = 10
